import { connect } from "react-redux";
import { Link } from "react-router-dom";

var RosterActions = require("lib/actions/RosterActions");
var RosterSelectors = require("lib/selectors/RosterSelectors");
var DataRouting = require("lib/routing/DataRouting");

var OrgSearch = require("components/appdev/wizards/OrgSearch");
var PaginationControls = require("components/utility/PaginationControls");
var Spinner = require("components/utility/Spinner");

require("appdev/wizards/school_org_list.scss");

// Convert params to conditions
function paramsToConditions(params) {
	var conditions = _.pick(params,
		"orgID", "name", "nameExact", "orgAdminID", "page"
	);

	conditions.perPage = params.perPage || DataRouting.PER_PAGE;
	conditions.page = params.page || 1;

	return conditions;
}

@connect(
	(state, props) => ({
		// get whether the component is currently loading school orgs from the state
		isLoading: RosterSelectors.isLoadingSchoolOrgs({ state, key: 'index' }),
	}),

	(dispatch, props) => ({
		fetchSchoolOrgsPaginated: (params, page) => {
			return dispatch(RosterActions.fetchSchoolOrgsPaginated({
				conditions: paramsToConditions(params),
				embeds: ["school_org.primary_admin", "school_org.school", "school.state", "school_org.student_count_cap", "school_org.teacher_count", "school_org.active_teacher_count", "school_org.student_count"],
				cache: true,
				page  // Specify the page number to fetch
			}))
		},

		// dispatches a selector to get school orgs with pagination.
		selectSchoolOrgs: (params) => {
			var conditions = paramsToConditions(params)
			return dispatch((dispatch, getState) => {
				return RosterSelectors.schoolOrgsPaginated({ state: getState(), page: conditions.page, conditions })
			})
		},

		// dispatches a selector to get the total number of school orgs
		schoolOrgsTotal: (params) => {
			var conditions = paramsToConditions(params)
			return dispatch((dispatch, getState) => {
				return RosterSelectors.schoolOrgsTotal({ state: getState(), conditions })
			})
		}
	})
)

class SchoolOrgList extends React.Component {
	constructor(props) {
		super();
		autobind(this);
		this.state = { 
			currentPage: 1, 
			paginatedSchoolOrgs: [], 
			params: {},
			searchProgress: { completed: 0, total: 0, isSearching: false },
			collapsedSections: {} // Track which search term sections are collapsed
		}
	}

	////////// HELPER FUNCTIONS //////////
	
	toggleSection(index) {
		this.setState(prevState => ({
			collapsedSections: {
				...prevState.collapsedSections,
				[index]: !prevState.collapsedSections[index]
			}
		}));
	}
	
	displaySchoolOrgName(schoolOrg) {
		var route = "/school_orgs/" + schoolOrg.id + "/details/controls";
		return (
			<Link to={route} target="_blank">
				{schoolOrg.school.name} ({schoolOrg.school.state})
			</Link>
		);
	}

	displayAdminName(admin) {
		if (admin) {
			var route = "/users/" + admin.id + "/details";
			return (
				<Link to={route}>
					{admin.first_name} {admin.last_name}
				</Link>
			);
		}
	}

	////////// STATE CHANGERS //////////
	
	// changes page from pagination component
	changePage(newPage) {
		var { params } = this.state;
		var newSearchParams = { ...params, page: newPage };

		// update the current page in state
		this.setState({ currentPage: newPage });

		// call selectSchoolOrgs with the updated search parameters
		var selectedSchoolOrgs = this.props.selectSchoolOrgs(newSearchParams);

		// check if any school orgs were returned
		if (_.size(selectedSchoolOrgs)) {
			// if there are school orgs, update the state with the new search parameters and the selected school orgs
			this.setState({ params: newSearchParams, paginatedSchoolOrgs: selectedSchoolOrgs });
		} else {
			// if there are no school orgs, fetch the paginated school orgs with the updated search parameters
			this.props.fetchSchoolOrgsPaginated(newSearchParams, newPage)
				.then(() => {
					// after fetching the school orgs, call selectSchoolOrgs again with the updated search parameters
					return this.props.selectSchoolOrgs(newSearchParams);
				})
				.then(selectedSchoolOrgs => {
					// update the state with the new search parameters and the selected school orgs
					this.setState({ params: newSearchParams, paginatedSchoolOrgs: selectedSchoolOrgs });
				});
		}
	}

	onSubmitSearch(params) {
		// Handle fuzzy search mode differently
		if (params.searchMode === 'fuzzy' && params.fuzzySchoolNames) {
			this.handleFuzzySearch(params.fuzzySchoolNames);
		} else {
			// call fetchSchoolOrgsPaginated with params and page number 1
			this.props.fetchSchoolOrgsPaginated(params, 1)
				// update local state
				.then(() => {
					this.setState({ params, currentPage: 1, paginatedSchoolOrgs: this.props.selectSchoolOrgs(params) })
				})
				// iff the promise is rejected, log the error to the console
				.catch((error) => {
					console.error("Error fetching school organizations", error);
				});
		}
	}

	handleFuzzySearch(schoolNames) {
		// Initialize search progress
		this.setState({ 
			searchProgress: { completed: 0, total: schoolNames.length, isSearching: true }
		});

		// Fetch school orgs for each school name and track which keyword matched
		var completedCount = 0;
		var searchPromises = schoolNames.map(schoolName => {
			var searchParams = { name: schoolName, perPage: 100 }; // Use larger perPage for fuzzy matches
			return this.props.fetchSchoolOrgsPaginated(searchParams, 1)
				.then(() => {
					var results = this.props.selectSchoolOrgs(searchParams);
					// Update progress
					completedCount++;
					this.setState({ 
						searchProgress: { completed: completedCount, total: schoolNames.length, isSearching: true }
					});
					// Tag each result with the search keyword that found it
					return { keyword: schoolName, results: results || [] };
				})
				.catch(error => {
					console.error(`Error searching for "${schoolName}"`, error);
					// Update progress even on error
					completedCount++;
					this.setState({ 
						searchProgress: { completed: completedCount, total: schoolNames.length, isSearching: true }
					});
					return { keyword: schoolName, results: [] };
				});
		});

		Promise.all(searchPromises)
			.then(searchResults => {
				// Store in state with categorized results and mark search as complete
				this.setState({ 
					params: { searchMode: 'fuzzy', fuzzySchoolNames: schoolNames },
					currentPage: 1, 
					fuzzySearchResults: searchResults,  // Array of { keyword, results }
					searchProgress: { completed: schoolNames.length, total: schoolNames.length, isSearching: false }
				});
			})
			.catch(error => {
				console.error("Error in fuzzy search", error);
				this.setState({ 
					searchProgress: { completed: schoolNames.length, total: schoolNames.length, isSearching: false }
				});
			});
	}

	////////// RENDER METHODS //////////

	// renders a table row for each school org in the state
	renderSchoolOrg(schoolOrg) {
		// extract relevant data from the school org object
		var admin = schoolOrg.primary_admin;
		var activeTeachers = schoolOrg.active_teacher_count;
		var totalTeachers = schoolOrg.teacher_count;

		// calculate the percentage of active teachers
		var teacherPercent;
		if (totalTeachers > 0) {
			teacherPercent = `(${Math.round((activeTeachers / totalTeachers) * 100)}%)`;
		}

		// render the table row with the extracted data
		return (
			<tr key={schoolOrg.id}>
				<td>
					{this.displaySchoolOrgName(schoolOrg)}
					<br />
					{this.displayAdminName(admin)}
				</td>
				<td>
					<ul>
						<li>School Org ID: {schoolOrg.id}</li>
						<li>Admin ID: {admin ? admin.id : "-"}</li>
						<li>
							Student Cap: {schoolOrg.student_count || "-"}
							/{schoolOrg.student_count_cap || "???"}
						</li>
						<li>
							Active Teachers: {activeTeachers || "-"}/{totalTeachers || "-"} {teacherPercent}
						</li>
					</ul>
				</td>
			</tr>
		);
	}

	// controlls pagination
	renderPagination() {
		var { params, currentPage } = this.state
		
		// Don't show pagination for fuzzy search
		if (params.searchMode === 'fuzzy') {
			return null;
		}
		
		var schoolOrgsTotal = this.props.schoolOrgsTotal(params) || 0

		if (schoolOrgsTotal < 1 || this.props.isLoading) { return null }

		return (
			<PaginationControls page={currentPage} perPage={params.perPage}
				totalCount={schoolOrgsTotal}
				onPageChange={this.changePage} />
		)
	}

	// renders list of school orgs
	renderSchoolOrgList() {
		var { params } = this.state;
		var isFuzzySearch = params.searchMode === 'fuzzy';
		
		if (this.props.isLoading) {
			return <Spinner placeholder={true} scale={1.7} />
		} else if (isFuzzySearch) {
			return this.renderFuzzySearchResults();
		} else {
			return (
				<div className="schoolOrgList">
					<table>
						<tbody>
							{this.state.paginatedSchoolOrgs.map((schoolOrg) => {
								return this.renderSchoolOrg(schoolOrg)
							})}
						</tbody>
					</table>
				</div>
			)
		}
	}

	renderFuzzySearchResults() {
		var { fuzzySearchResults, searchProgress, collapsedSections } = this.state;
		
		// Show progress indicator while searching
		if (searchProgress.isSearching) {
			return (
				<div className="schoolOrgList">
					<div className="fuzzySearchProgress">
						<Spinner placeholder={false} scale={1.2} />
						<div className="progressText">
							<strong>Searching...</strong>
							<div className="progressCount">
								Completed {searchProgress.completed} of {searchProgress.total} searches
							</div>
						</div>
						<div className="progressBar">
							<div 
								className="progressBarFill" 
								style={{ width: `${(searchProgress.completed / searchProgress.total) * 100}%` }}
							/>
						</div>
					</div>
				</div>
			);
		}
		
		if (!fuzzySearchResults || fuzzySearchResults.length === 0) {
			return (
				<div className="schoolOrgList">
					<p>No matching school organizations found for the provided school names.</p>
				</div>
			);
		}

		var totalResults = fuzzySearchResults.reduce((sum, item) => sum + item.results.length, 0);

		return (
			<div className="schoolOrgList">
				<div className="fuzzySearchSummary">
					<strong>Found {totalResults} school organization(s) across {fuzzySearchResults.length} search term(s)</strong>
				</div>
				
				{fuzzySearchResults.map((searchResult, index) => {
					var { keyword, results } = searchResult;
					var isCollapsed = collapsedSections[index];
					
					if (results.length === 0) {
						return (
							<div key={index} className="fuzzySearchGroup">
								<div className="fuzzySearchKeyword">
									<span className="keywordLabel">"{keyword}"</span>
									<span className="resultCount">No matches found</span>
								</div>
							</div>
						);
					}
					
					return (
						<div key={index} className="fuzzySearchGroup">
							<div 
								className="fuzzySearchKeyword clickable" 
								onClick={() => this.toggleSection(index)}
							>
								<div className="keywordLabelGroup">
									<span className={`collapseIcon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
										{isCollapsed ? '▶' : '▼'}
									</span>
									<span className="keywordLabel">"{keyword}"</span>
								</div>
								<span className="resultCount">{results.length} match{results.length !== 1 ? 'es' : ''}</span>
							</div>
							{!isCollapsed && (
								<table>
									<tbody>
										{results.map((schoolOrg) => {
											return this.renderSchoolOrg(schoolOrg)
										})}
									</tbody>
								</table>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	render() {
		return (
			<div className="schoolListContainer">
				<OrgSearch orgType='schoolOrg' onSubmit={this.onSubmitSearch} />
				{this.renderPagination()}
				{this.renderSchoolOrgList()}
			</div>
		);
	}

};

SchoolOrgList.propTypes = PageStrictPropTypes({
});

module.exports = SchoolOrgList;


// WEBPACK FOOTER //
// ./modules/components/appdev/wizards/SchoolOrgList.js